/**
 * YouTube Loop Player
 * 
 * A loop player for YouTube videos using the YouTube IFrame API.
 * Uses the same UI/UX patterns as video-loop-player and audio-loop-player.
 * 
 * @element youtube-loop-player
 * 
 * @prop {string} videoId - YouTube video ID (required)
 * @prop {boolean} showShortcuts - Show keyboard shortcuts help (default: true)
 * @prop {boolean} autoRestore - Restore saved state from localStorage (default: true)
 * @prop {string} storagePrefix - Prefix for storage keys (default: 'ytloop')
 * 
 * @fires loop-created - When a new loop is created
 * @fires loop-deleted - When a loop is deleted
 * @fires playback-state-changed - When play/pause state changes
 */

import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LoopPlayerMixin, loopPlayerStyles } from '../loop-player-base/index.js';

// Helper to get the YouTube API from window
function getYoutubeApi(): typeof YT {
  return (window as unknown as { YT: typeof YT }).YT;
}

@customElement('youtube-loop-player')
export class YouTubeLoopPlayer extends LoopPlayerMixin(LitElement) {
  static styles = [
    ...loopPlayerStyles,
    css`
      /* YouTube-specific styles */
      .lp-media-wrapper iframe {
        border: 0;
      }
    `
  ];

  // ─── Public Properties ───────────────────────────────────────────────────

  @property({ type: String })
  videoId = '';

  @property({ type: Boolean })
  showShortcuts = true;

  @property({ type: Boolean })
  autoRestore = true;

  @property({ type: String })
  storagePrefix = 'ytloop';

  /** Hide padding and use minimal styling (for embedded contexts) */
  @property({ type: Boolean })
  compact = false;

  // ─── Private State ───────────────────────────────────────────────────────

  @state() private _playerReady = false;
  @state() private _videoTitle = '';
  @state() private _channelName = '';

  private _player: YT.Player | null = null;

  // ─── Abstract Implementation: Compact View ───────────────────────────────

  readonly mediaIcon = '📺';

  getCompactTitle(): string {
    return this._videoTitle || `YouTube: ${this.videoId}`;
  }

  /** Get channel name for subtitle display */
  getSubtitle(): string {
    return this._channelName;
  }

  // ─── Abstract Method Implementations ─────────────────────────────────────

  getMediaId(): string {
    return this.videoId;
  }

  play(): void {
    this._player?.playVideo();
  }

  pause(): void {
    this._player?.pauseVideo();
  }

  getCurrentTime(): number {
    return this._player?.getCurrentTime?.() ?? 0;
  }

  getDuration(): number {
    return this._player?.getDuration?.() ?? 0;
  }

  seekTo(time: number): void {
    this._player?.seekTo(Math.max(0, Math.min(this.duration, time)), true);
  }

  setPlaybackRate(rate: number): void {
    this._player?.setPlaybackRate(rate);
  }

  isMediaReady(): boolean {
    return this._playerReady;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();
    this._loadYouTubeAPI();
    this._fetchVideoTitle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._player) {
      this._player.destroy();
      this._player = null;
    }
    this._playerReady = false;
  }

  firstUpdated() {
    // Only init immediately if already expanded (player element must be visible)
    if (this.expanded) {
      const YT = getYoutubeApi();
      if (YT?.Player) {
        this._initPlayer();
      }
    }
  }

  // Called by mixin when expanded changes from false to true
  onFirstExpand() {
    const YT = getYoutubeApi();
    if (YT?.Player && !this._player) {
      this._initPlayer();
    }
  }

  // ─── YouTube API ─────────────────────────────────────────────────────────

  private _loadYouTubeAPI() {
    const YT = getYoutubeApi();
    if (YT?.Player) {
      if (this.hasUpdated) {
        this._initPlayer();
      }
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    (window as unknown as { onYouTubeIframeAPIReady: () => void }).onYouTubeIframeAPIReady = () => {
      if (this.hasUpdated) {
        this._initPlayer();
      }
    };
  }

  private _initPlayer() {
    if (this._player) return;

    const YT = getYoutubeApi();
    const playerElement = this.shadowRoot?.getElementById(`player-${this.videoId}`);

    if (!playerElement) {
      console.error('[YouTubeLoopPlayer] Player element not found');
      return;
    }

    this._player = new YT.Player(playerElement, {
      height: '360',
      width: '640',
      videoId: this.videoId,
      playerVars: {
        enablejsapi: 1,
        origin: window.location.origin,
        rel: 0,
        modestbranding: 1,
      },
      events: {
        onReady: (event) => this._onPlayerReady(event),
        onStateChange: (event) => this._onPlayerStateChange(event),
        onError: (event) => this._onPlayerError(event),
      },
    });
  }

  private _onPlayerReady(_event: YT.PlayerEvent) {
    if (!this._player) return;
    this._playerReady = true;
    this.duration = this._player.getDuration();
    this.startTimeUpdate();

    if (this.autoRestore) {
      // Preserve current expanded state - user explicitly expanded to trigger player init
      const currentExpanded = this.expanded;
      this.restoreState();
      this.expanded = currentExpanded;
    }

    // Check for pending autoplay (user clicked play while collapsed)
    this._onMediaReady();
  }

  /** Fetch video title and channel via YouTube oEmbed API (no API key required) */
  private async _fetchVideoTitle() {
    if (!this.videoId || this._videoTitle) return;
    try {
      const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${this.videoId}&format=json`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.title) {
          this._videoTitle = data.title;
        }
        if (data.author_name) {
          this._channelName = data.author_name;
        }
      }
    } catch {
      // Fallback: title stays empty, getCompactTitle() will show videoId
    }
  }

  private _onPlayerStateChange(event: YT.OnStateChangeEvent) {
    const YT = getYoutubeApi();
    const wasPlaying = this.isPlaying;
    this.isPlaying = event.data === YT.PlayerState.PLAYING;

    if (wasPlaying !== this.isPlaying) {
      this.dispatchEvent(new CustomEvent('playback-state-changed', {
        detail: { playing: this.isPlaying },
        bubbles: true,
        composed: true,
      }));
    }
  }

  private _onPlayerError(event: YT.OnErrorEvent) {
    const errorMessages: Record<number, string> = {
      2: 'Invalid video ID',
      5: 'HTML5 player error',
      100: 'Video not found or private',
      101: 'Video owner does not allow embedding',
      150: 'Video owner does not allow embedding',
    };
    const message = errorMessages[event.data] ?? `Unknown error (code: ${event.data})`;
    console.error('[YouTubeLoopPlayer] Error:', message);
  }

  // YouTube-specific: blur iframe to capture keyboard events
  private _blurIframe() {
    const iframe = this.shadowRoot?.querySelector('iframe');
    if (iframe && document.activeElement === iframe) {
      (document.activeElement as HTMLElement).blur();
    }
  }

  // Override keyboard handler to blur iframe first
  handleKeyboard(e: KeyboardEvent) {
    this._blurIframe();
    super.handleKeyboard(e);
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  // All render methods are now in the mixin

  render() {
    // Structure: header → video (full mode) → controls+timeline → loop panel
    return html`
      <div class="lp-container">
        ${this._renderHeader()}
        
        <div class="lp-full-content">
          <div class="lp-media-wrapper">
            <div id="player-${this.videoId}"></div>
            ${this.countdown !== null ? html`
              <div class="lp-countdown-overlay" @click=${() => this.cancelCountdown()}>
                <span class="lp-countdown-number">${this.countdown}</span>
              </div>
            ` : null}
          </div>
        </div>

        <div style="position: relative;">
          ${this._renderControlsAndTimeline()}

          ${!this.expanded && this.countdown !== null ? html`
            <div class="lp-countdown-standalone" @click=${() => this.cancelCountdown()}>
              <span class="lp-countdown-number">${this.countdown}</span>
            </div>
          ` : null}
        </div>

        <div class="lp-controls-panel">
          ${this._renderEditPanel()}
          ${this._renderShortcuts()}
        </div>
      </div>
      ${this._renderDeleteDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'youtube-loop-player': YouTubeLoopPlayer;
  }
}
